import {
  Text,
  Flex,
  Center,
  HoverCard,
  Paper,
  useMatches,
  Box,
  getContrastColor,
  useMantineTheme,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { ScrollContainer } from "../../../../../../-components/scroll-container"
import { SuspenseWithErrorBoundary } from "../../../../../../-components/suspense"
import { dateToDateOnly } from "../../../../../../utils/date-to-date-only"
import { useBreakpoints } from "../../../../../../utils/use-breakpoints"

import { CalendarAppointmentsSegmentedIndicator } from "./calendar-appointments-segmented-indicator"

import classes from "./index.module.css"
import { CalendarAppointment } from "../../../../../../@client/Theasoft_ApiServer_Calendar"
import { AbsenceIndicator } from "../../absence-indicator"
import {
  useBackgroundColorForDate,
  useSchoolHolidayColor,
} from "../../useColorForDate"
import { AppointmentStack } from "../../../appointment-stack"
import { getFormattedDate } from "@/utils/get-formatted-date"
import { DefaultModal } from "@components/default-modal"

export function RenderDate({
  date,
  dimmed,
  appointments,
  status,
  isHoliday,
  isSchoolHoliday,
}: {
  date: Date
  dimmed: boolean
  appointments: CalendarAppointment[]
  status: CalendarAppointment[]
  isSchoolHoliday?: boolean
  isHoliday?: boolean
}) {
  const textSize = useMatches({
    base: "lg",
    sm: "sm",
  })

  const { xs } = useBreakpoints()
  const [modalOpened, { open: openModal, close: closeModal }] =
    useDisclosure(false)

  const bgColorForDate = useBackgroundColorForDate(date, isHoliday)
  const schoolHolidayColor = useSchoolHolidayColor()
  const bg =
    bgColorForDate ?? (isSchoolHoliday ? schoolHolidayColor : undefined)

  const theme = useMantineTheme()

  const textcolor = getContrastColor({ theme, color: bg })

  const dateCell = (
    <Paper
      withBorder
      shadow="sm"
      className={classes.dayPaper}
      bg={bg}
      style={{
        height: "100%",
        width: "100%",
        borderRadius: "5px",
        border:
          dateToDateOnly(date) === dateToDateOnly(new Date())
            ? "1px solid red"
            : undefined,
      }}
    >
      <Flex h="100%" direction="column">
        <AbsenceIndicator
          status={status}
          orientation="row"
          w="100%"
          mih="10px"
          borderRadius="5px 5px 0 0 "
        />
        <Center
          style={{
            flexDirection: "column",
            justifyContent: "space-evenly",
          }}
          flex="1"
        >
          <Text c={bg ? textcolor : undefined} size={textSize}>
            {date.getDate()}
          </Text>
          <CalendarAppointmentsSegmentedIndicator appointments={appointments} />
        </Center>
      </Flex>
    </Paper>
  )

  return (
    <Box
      onClick={(ev) => {
        ev.preventDefault()
        ev.stopPropagation()
      }}
      p="0.2rem"
      opacity={dimmed ? "0.4" : undefined}
    >
      {xs ? (
        <>
          <div onClick={openModal}>{dateCell}</div>
          <DefaultModal
            opened={modalOpened}
            onClose={closeModal}
            title={getFormattedDate(date)}
            centered
          >
            <SuspenseWithErrorBoundary>
              <AppointmentStack appointments={[...appointments, ...status]} />
            </SuspenseWithErrorBoundary>
          </DefaultModal>
        </>
      ) : (
        <HoverCard openDelay={100} closeDelay={100} withArrow shadow="md">
          <HoverCard.Target>{dateCell}</HoverCard.Target>
          <HoverCard.Dropdown>
            <SuspenseWithErrorBoundary>
              <ScrollContainer mah={"fit-content"}>
                <AppointmentStack appointments={[...appointments, ...status]} />
              </ScrollContainer>
            </SuspenseWithErrorBoundary>
          </HoverCard.Dropdown>
        </HoverCard>
      )}
    </Box>
  )
}
